import React, { useEffect, useMemo, useRef, useState } from "react";

const Paragraph = ({ children, className, lines = 2 }) => {
  const containerRef = useRef(null);
  const extractRef = useRef(null);
  const measureRef = useRef(null);
  const hideRef = useRef(null);

  const [expanded, setExpanded] = useState(false);
  const [fullText, setFullText] = useState("");
  const [displayText, setDisplayText] = useState("");
  const [canToggle, setCanToggle] = useState(false);
  const [hideWidth, setHideWidth] = useState(0);
  const [padRight, setPadRight] = useState(0);
  const [padBottom, setPadBottom] = useState(0);
  const [lineHeightPx, setLineHeightPx] = useState(0);
  const [bgColor, setBgColor] = useState("transparent");

  const clampLines = useMemo(() => {
    const value = Number(lines);
    if (!Number.isFinite(value)) return 2;
    return Math.max(0, Math.floor(value));
  }, [lines]);

  useEffect(() => {
    const node = extractRef.current;
    if (!node) return;
    const nextText = (node.innerText || node.textContent || "").trim();
    setFullText(nextText);
  }, [children]);

  useEffect(() => {
    if (clampLines <= 0) setExpanded(false);
  }, [clampLines]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const update = () => {
      const computed = window.getComputedStyle(container);
      setPadRight(Number.parseFloat(computed.paddingRight) || 0);
      setPadBottom(Number.parseFloat(computed.paddingBottom) || 0);

      const fontSize = Number.parseFloat(computed.fontSize) || 0;
      const rawLineHeight = computed.lineHeight;
      const lh =
        rawLineHeight === "normal"
          ? fontSize
            ? fontSize * 1.2
            : 0
          : Number.parseFloat(rawLineHeight);
      setLineHeightPx(Number.isFinite(lh) ? lh : 0);

      const bg = computed.backgroundColor || "transparent";
      setBgColor(bg === "rgba(0, 0, 0, 0)" ? "transparent" : bg);
    };

    update();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => update());
      resizeObserver.observe(container);
    }

    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      resizeObserver?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!expanded || !canToggle) return;
    const el = hideRef.current;
    if (!el) return;

    const update = () => {
      const node = hideRef.current;
      if (!node) return;
      setHideWidth(node.getBoundingClientRect().width);
    };

    update();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => update());
      resizeObserver.observe(el);
    }

    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      resizeObserver?.disconnect();
    };
  }, [expanded, canToggle]);

  useEffect(() => {
    const container = containerRef.current;
    const measurer = measureRef.current;
    if (!container || !measurer) return;

    const getLineHeightPx = (computed) => {
      const fontSize = Number.parseFloat(computed.fontSize) || 0;
      const rawLineHeight = computed.lineHeight;
      if (rawLineHeight === "normal") return fontSize ? fontSize * 1.2 : 0;
      const parsed = Number.parseFloat(rawLineHeight);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const setMeasureStyles = () => {
      const computed = window.getComputedStyle(container);
      measurer.style.font = computed.font;
      measurer.style.fontFamily = computed.fontFamily;
      measurer.style.fontSize = computed.fontSize;
      measurer.style.fontWeight = computed.fontWeight;
      measurer.style.letterSpacing = computed.letterSpacing;
      measurer.style.lineHeight = computed.lineHeight;
      measurer.style.wordBreak = computed.wordBreak;
      measurer.style.whiteSpace = "normal";
      measurer.style.boxSizing = "content-box";
      measurer.style.padding = "0";
      measurer.style.border = "0";

      const paddingLeft = Number.parseFloat(computed.paddingLeft) || 0;
      const paddingRight = Number.parseFloat(computed.paddingRight) || 0;
      const contentWidth = Math.max(
        0,
        container.clientWidth - paddingLeft - paddingRight
      );
      measurer.style.width = `${contentWidth}px`;

      return { computed, lineHeightPx: getLineHeightPx(computed) };
    };

    const measureHeight = (value) => {
      measurer.textContent = value;
      return measurer.scrollHeight;
    };

    const compute = () => {
      if (expanded) {
        setDisplayText(fullText);
        return;
      }

      const { lineHeightPx } = setMeasureStyles();
      if (!fullText || clampLines <= 0 || !lineHeightPx) {
        setDisplayText(fullText);
        setCanToggle(false);
        return;
      }

      const maxHeight = clampLines * lineHeightPx;
      const fullHeight = measureHeight(fullText);

      if (fullHeight <= maxHeight + 1) {
        setDisplayText(fullText);
        setCanToggle(false);
        return;
      }

      setCanToggle(true);

      const suffix = "…Show";

      let low = 0;
      let high = fullText.length;
      let best = 0;

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const candidate = fullText.slice(0, mid).trimEnd();
        const h = measureHeight(`${candidate}${suffix}`);

        if (h <= maxHeight + 1) {
          best = mid;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      let finalText = fullText.slice(0, best).trimEnd();
      if (finalText && finalText.length < fullText.length) {
        const wordCut = finalText.replace(/\s+\S*$/, "").trimEnd();
        if (wordCut && wordCut.length >= Math.max(1, finalText.length - 20)) {
          finalText = wordCut;
        }
      }

      setDisplayText(finalText);
    };

    compute();

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => compute());
      resizeObserver.observe(container);
    }

    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("resize", compute);
      resizeObserver?.disconnect();
    };
  }, [fullText, clampLines, expanded]);

  return (
    <div ref={containerRef} className={`relative ${className || ""}`}>
      {expanded ? (
        <>
          <div
            style={{ paddingRight: canToggle ? Math.ceil(hideWidth) + 4 : 0 }}
          >
            {children}
          </div>
          {canToggle && (
            <button
              ref={hideRef}
              type="button"
              className="bg-transparent border-0 outline-none text-primary hover:text-primary-600 text-xs absolute p-0 m-0"
              onClick={() => setExpanded(false)}
              style={{
                right: `${padRight}px`,
                bottom: `${padBottom}px`,
                height: lineHeightPx ? `${lineHeightPx}px` : undefined,
                lineHeight: lineHeightPx ? `${lineHeightPx}px` : "inherit",
                display: "flex",
                alignItems: "center",
                backgroundColor: bgColor,
              }}
            >
              Hide
            </button>
          )}
        </>
      ) : (
        <>
          {displayText}
          {canToggle && (
            <>
              {"…"}
              <button
                type="button"
                className="bg-transparent border-0 outline-none text-primary hover:text-primary-600 p-0 m-0 ml-1 align-baseline inline"
                onClick={() => setExpanded(true)}
                style={{
                  font: "inherit",
                  lineHeight: "inherit",
                }}
              >
                Show
              </button>
            </>
          )}
        </>
      )}

      <div
        ref={extractRef}
        style={{
          position: "fixed",
          visibility: "hidden",
          top: -10000,
          left: -10000,
          pointerEvents: "none",
          zIndex: -1,
          whiteSpace: "normal",
        }}
      >
        {children}
      </div>

      <div
        ref={measureRef}
        style={{
          position: "fixed",
          visibility: "hidden",
          top: -10000,
          left: -10000,
          pointerEvents: "none",
          zIndex: -1,
          whiteSpace: "normal",
        }}
      />
    </div>
  );
};

export default Paragraph;
